import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { WorkflowData, initialWorkflowData } from "../../types/Workflow";
import { ListData } from "../../types/List";

interface ComponentProps {
  onSubmit: (data: Partial<WorkflowData>) => void;
  onClose: () => void;
  onBack: () => void;
  initialData?: Partial<WorkflowData>;
  transactionData: ListData[];
}

const WorkflowForm: React.FC<ComponentProps> = ({
  onSubmit,
  onClose,
  onBack,
  initialData,
  transactionData,
}) => {
  const [inputData, setInputData] = useState<Partial<WorkflowData>>(
    initialData || initialWorkflowData
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (initialData) {
      setInputData(initialData);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setInputData((prev: Partial<WorkflowData>) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!inputData.name) newErrors.name = "Workflow name is required";
    if (!inputData.transactionId)
      newErrors.transactionId = "Transaction is required";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(inputData);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      {/* 🔹 Responsive grid layout */}
      <Box
        display="grid"
        gridTemplateColumns={isMobile ? "1fr" : "repeat(1, 1fr)"}
        gap={2}
      >
        <Box>
          <TextField
            select
            name="transactionId"
            label="Transaction"
            value={inputData.transactionId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.transactionId}
            helperText={errors.transactionId}
          >
            <MenuItem key={0} value={0}>
              Please select
            </MenuItem>
            {transactionData.map((data) => (
              <MenuItem key={data.id} value={data.id}>
                {data.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            name="name"
            label="Name"
            value={inputData.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              "& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-input[readonly]":
                {
                  backgroundColor: "#dad6d6df", // light gray background
                  color: "#555", // slightly dim text color
                },
            }}
          />
          <TextField
            name="description"
            label="Description"
            value={inputData.description}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </Box>
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="primary" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default WorkflowForm;
